import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Eye, EyeOff, Loader2, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_readiness-check-27/artifacts/el8npnpt_CXOlogo.png";

const SetPasswordPage = () => {
  const navigate = useNavigate();
  const { user, getToken, refreshUser } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const requirements = [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'At least 1 number', met: /\d/.test(password) },
    { label: 'At least 1 special character', met: /[!@#$%^&*(),.?":{}|<>]/.test(password) }
  ];

  const allRequirementsMet = requirements.every(r => r.met);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const getStrength = () => {
    const metCount = requirements.filter(r => r.met).length;
    if (metCount === 3 && password.length >= 12) return { label: 'Strong', color: 'bg-green-500' };
    if (metCount >= 2) return { label: 'Fair', color: 'bg-yellow-500' };
    return { label: 'Weak', color: 'bg-red-500' };
  };

  const strength = getStrength();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!allRequirementsMet) {
      toast.error('Please meet all password requirements');
      return;
    }
    
    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { new_password: password },
        { headers: { Authorization: `Bearer ${getToken()}` } }
      );
      
      await refreshUser();
      toast.success('Password changed successfully');
      navigate('/org-profile');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-center">
          <img src={LOGO_URL} alt="CodeXero" className="h-10" />
        </div>
      </header>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center py-12 px-6">
        <div className="w-full max-w-md">
          <div className="bg-white border border-slate-200 rounded-sm p-8 shadow-[0_4px_24px_rgba(0,51,102,0.06)]">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-[800] text-[#003366] mb-2" data-testid="set-password-heading">
                Set Your New Password
              </h1>
              <p className="text-slate-600">
                Welcome, <span className="font-semibold">{user?.first_name}</span>! Please set a secure password.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="password" className="text-[#003366] font-medium">New Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    data-testid="new-password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="rounded-sm pr-10"
                    placeholder="Enter new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                
                {/* Strength Indicator */}
                {password.length > 0 && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${strength.color} transition-all`}
                          style={{ width: `${(requirements.filter(r => r.met).length / 3) * 100}%` }}
                        />
                      </div>
                      <span className={`text-sm font-medium ${
                        strength.label === 'Strong' ? 'text-green-600' :
                        strength.label === 'Fair' ? 'text-yellow-600' : 'text-red-600'
                      }`} data-testid="password-strength">
                        {strength.label}
                      </span>
                    </div>
                    <ul className="space-y-1">
                      {requirements.map((req, i) => (
                        <li 
                          key={i} 
                          className={`flex items-center gap-2 text-sm ${req.met ? 'text-green-600' : 'text-slate-500'}`}
                          data-testid={`requirement-${i}`}
                        >
                          {req.met ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                          {req.label}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="confirmPassword" className="text-[#003366] font-medium">Confirm Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    data-testid="confirm-password-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className={`rounded-sm pr-10 ${
                      confirmPassword.length > 0 && !passwordsMatch ? 'border-red-500' : ''
                    }`}
                    placeholder="Confirm new password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                {confirmPassword.length > 0 && !passwordsMatch && (
                  <p className="text-red-500 text-sm mt-1">Passwords do not match</p>
                )}
              </div>

              <Button
                type="submit"
                disabled={loading || !allRequirementsMet || !passwordsMatch}
                data-testid="set-password-submit-btn"
                className="w-full bg-[#003366] hover:bg-[#002244] text-white py-6 rounded-sm btn-hover disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Set Password & Continue'
                )}
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#003366] text-white py-6">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-400 text-sm">© CodeXero {new Date().getFullYear()}</p>
        </div>
      </footer>
    </div>
  );
};

export default SetPasswordPage;
